import { type ApiSchemas, call } from '@rental/contracts/client'
import {
  customerName,
  DATA_EXPORT_PURPOSES,
  type DataExportPurpose,
  ERASURE_REQUEST_CHANNELS,
  type ErasureRequestChannel,
  LEGAL_HOLD_REASONS,
  type LegalHoldReason,
} from '@rental/domain'
import { format, formatDate, formatDateTime, plural } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type RefObject, useRef, useState } from 'react'
import { AUTH_MODE, api } from '../api'
import { ChoiceChips } from '../components/ChoiceChips'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { OWNER_CODE, OwnerCodeField } from '../components/OwnerCodeField'
import { PendingLine } from '../components/PendingBadge'
import { Section } from '../components/Section'
import { LoadState } from '../components/states'
import { Facts, FIELD, ProblemNotice, StaffInput } from '../components/ui'
import { filePath, saveFromLink } from '../files'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { useRomeToday } from '../rome-today'
import { bookingHoldWords, romeDay, verdictWords } from './privacy'
import { customerChanged, loadPrivacy, privacyQueryKey } from './queries'

type Panel = ApiSchemas['CustomerPrivacyPanel']

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** An outcome said once per action, each taking the focus (UI-2g). */
type Said = { key: number; text: string }

/**
 * S8 (GDPR), the Owner's privacy panel on the Cliente page: what each part
 * of the customer's data does now and what keeps it, what the law keeps
 * whatever is asked, and the other records that may be the same person's.
 * A request to erase is recorded from what a request would do, with the day
 * it arrived, how, and the Owner's code; it can be withdrawn for 24 hours.
 * A legal hold is placed with its reason and lifted with the code. A copy of
 * the data is exported with the code too. Every answer is the panel as it
 * now stands: the screen draws it, never guesses.
 */
export function PrivacyPanel({ customerId }: { customerId: string }) {
  const { messages: m } = useLocale()
  const panel = useQuery({
    queryKey: privacyQueryKey(customerId),
    queryFn: () => loadPrivacy(customerId),
    // Personal data and what keeps it: read afresh whenever the record is.
    staleTime: 0,
  })
  return (
    <Section title={m.privacy.title}>
      <p className="max-w-prose text-sm text-text-muted">{m.privacy.intro}</p>
      <LoadState data={panel.data} problem={problemOf(panel.error)} bare>
        {(data) => <PanelBody panel={data} />}
      </LoadState>
    </Section>
  )
}

function PanelBody({ panel }: { panel: Panel }) {
  const { locale, messages: m } = useLocale()
  const copy = m.privacy
  const queryClient = useQueryClient()
  const requestDialog = useRef<HTMLDialogElement>(null)
  const withdrawDialog = useRef<HTMLDialogElement>(null)
  const holdDialog = useRef<HTMLDialogElement>(null)
  const liftDialog = useRef<HTMLDialogElement>(null)
  const exportDialog = useRef<HTMLDialogElement>(null)
  const [said, setSaid] = useState<Said | null>(null)
  const { request, legalHold } = panel
  const anonymised = panel.anonymisedAt !== null

  /** The panel as the answer left it, said in words; the record and its lists read again. */
  function changed(next: Panel, text: string) {
    queryClient.setQueryData(privacyQueryKey(panel.customerId), next)
    setSaid({ key: Date.now(), text })
    void customerChanged(queryClient, panel.customerId)
  }

  return (
    <>
      {said ? (
        <OutcomeNotice key={said.key}>
          <p>{said.text}</p>
        </OutcomeNotice>
      ) : null}
      {panel.anonymisedAt !== null ? (
        <p>{format(copy.anonymised, { date: romeDay(locale, panel.anonymisedAt) })}</p>
      ) : null}
      {request ? (
        <div className="flex flex-col gap-1">
          <p>
            {format(copy.requested, {
              date: formatDate(locale, request.requestedOn, 'short'),
              channel: copy.channels[request.channel],
              by: request.recordedBy,
              at: formatDateTime(locale, request.recordedAt),
            })}
          </p>
          {request.withdrawable ? (
            <p>
              {format(copy.withdrawable, { until: formatDateTime(locale, request.graceEndsAt) })}
            </p>
          ) : null}
        </div>
      ) : null}
      {legalHold ? (
        <Notice tone="warning">
          <p>
            {format(copy.hold, {
              date: romeDay(locale, legalHold.at),
              by: legalHold.by,
              reason: copy.reasons[legalHold.reason],
            })}
          </p>
        </Notice>
      ) : null}
      <Facts
        items={[
          [copy.parts.contact, verdictWords(panel.contact, locale, m)],
          [copy.parts.identity, verdictWords(panel.identity, locale, m)],
        ]}
      />
      <p>
        {format(copy.keptByLaw, {
          contracts: plural(locale, copy.contracts, panel.keptByLaw.contracts),
          letters: plural(locale, copy.letters, panel.keptByLaw.fineLetters),
          fiscal: plural(locale, copy.fiscal, panel.keptByLaw.fiscalDocuments),
        })}
      </p>
      {panel.identityPhotos > 0 ? <p>{plural(locale, copy.photos, panel.identityPhotos)}</p> : null}
      {panel.bookings.length > 0 ? (
        <>
          <h3 className="text-lg">{copy.bookings.title}</h3>
          <ul className="flex flex-col gap-2">
            {panel.bookings.map((booking) => (
              <li key={booking.code} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <BookingCode code={booking.code} />
                <span className="text-sm text-text-muted">{copy.bookings[booking.role]}</span>
                <span className="text-sm">
                  {booking.holds.length > 0
                    ? format(copy.bookings.held, { holds: bookingHoldWords(booking.holds, m) })
                    : copy.bookings.free}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {panel.duplicates.length > 0 ? (
        <>
          <h3 className="text-lg">{copy.duplicates.title}</h3>
          <p className="text-sm text-text-muted">{copy.duplicates.intro}</p>
          <ul className="flex flex-col gap-2">
            {panel.duplicates.map((other) => (
              <li key={other.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Link
                  to="/customers/$customerId"
                  params={{ customerId: other.id }}
                  className="touch-target font-medium"
                >
                  {customerName(other)}
                </Link>
                <span className="text-sm text-text-muted">
                  {other.matches.map((match) => copy.duplicates[match]).join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div className="flex flex-wrap gap-3">
        {!anonymised && request === null ? (
          <OpenButton dialog={requestDialog} label={copy.request.action} />
        ) : null}
        {request?.withdrawable ? (
          <OpenButton dialog={withdrawDialog} label={copy.withdraw.action} />
        ) : null}
        {!anonymised && legalHold === null ? (
          <OpenButton dialog={holdDialog} label={copy.legalHold.action} />
        ) : null}
        {legalHold ? <OpenButton dialog={liftDialog} label={copy.lift.action} /> : null}
        {!anonymised && AUTH_MODE === 'cookie' ? (
          <OpenButton dialog={exportDialog} label={copy.export.action} />
        ) : null}
      </div>
      {!anonymised && AUTH_MODE === 'token' ? (
        <PendingLine>{copy.export.notInApp}</PendingLine>
      ) : null}
      <RequestDialog dialogRef={requestDialog} panel={panel} onDone={changed} />
      <WithdrawDialog dialogRef={withdrawDialog} panel={panel} onDone={changed} />
      <HoldDialog dialogRef={holdDialog} panel={panel} onDone={changed} />
      <LiftDialog dialogRef={liftDialog} panel={panel} onDone={changed} />
      {!anonymised && AUTH_MODE === 'cookie' ? (
        <ExportDialog
          dialogRef={exportDialog}
          customerId={panel.customerId}
          onDone={(text) => setSaid({ key: Date.now(), text })}
        />
      ) : null}
    </>
  )
}

function OpenButton({
  dialog,
  label,
}: {
  dialog: RefObject<HTMLDialogElement | null>
  label: string
}) {
  return (
    <Button variant="outline" className="rounded-sm" onClick={() => dialog.current?.showModal()}>
      {label}
    </Button>
  )
}

type DialogProps = {
  dialogRef: RefObject<HTMLDialogElement | null>
  panel: Panel
  onDone: (panel: Panel, text: string) => void
}

/**
 * The request, recorded from what it would do (the preview), with the day it
 * arrived (the month to answer runs from it), how it arrived, and the
 * Owner's code. Another record with the same e-mail or phone is pointed out:
 * a request reaches each of the person's records only when each is asked.
 */
function RequestDialog({ dialogRef, panel, onDone }: DialogProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.privacy.request
  const today = useRomeToday()
  const { send, busy, problem, clear } = useSend()
  const [requestedOn, setRequestedOn] = useState(today)
  const [channel, setChannel] = useState<ErasureRequestChannel | null>(null)
  const [code, setCode] = useState('')
  const reasons = [
    ...(ISO_DATE.test(requestedOn) && requestedOn <= today ? [] : [copy.why.day]),
    ...(channel ? [] : [copy.why.channel]),
    ...(OWNER_CODE.test(code) ? [] : [copy.why.code]),
  ]

  async function confirm() {
    if (!channel || reasons.length > 0) return
    const next = await send(() =>
      call(
        api.POST('/v1/staff/customers/{customerId}/privacy/request', {
          params: { path: { customerId: panel.customerId } },
          body: { requestedOn, channel, code },
        }),
      ),
    )
    // A code is spent whether or not the server took the request: a new one is typed either way.
    setCode('')
    if (!next) return
    dialogRef.current?.close()
    setChannel(null)
    setRequestedOn(today)
    onDone(
      next,
      format(copy.done, {
        until: next.request ? formatDateTime(locale, next.request.graceEndsAt) : '',
      }),
    )
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={copy.title}
      confirmLabel={copy.confirm}
      cancelLabel={m.privacy.cancel}
      busy={busy}
      ready={reasons.length === 0}
      reasons={reasons}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        setCode('')
      }}
      className="max-w-lg"
    >
      {panel.preview ? (
        <>
          <p>{copy.preview}</p>
          <Facts
            items={[
              [m.privacy.parts.contact, verdictWords(panel.preview.contact, locale, m)],
              [m.privacy.parts.identity, verdictWords(panel.preview.identity, locale, m)],
            ]}
          />
        </>
      ) : null}
      <p className="text-sm">{copy.grace}</p>
      {panel.duplicates.length > 0 ? (
        <Notice tone="warning">
          <p>{plural(locale, copy.duplicates, panel.duplicates.length)}</p>
        </Notice>
      ) : null}
      <Field
        className={FIELD}
        label={copy.requestedOn}
        hint={copy.requestedOnHint}
        required
        error={requestedOn !== '' && requestedOn > today ? copy.why.day : null}
      >
        {(control) => (
          <StaffInput
            {...control}
            type="date"
            max={today}
            value={requestedOn}
            onChange={(event) => setRequestedOn(event.target.value)}
          />
        )}
      </Field>
      <ChoiceChips
        name="erasure-request-channel"
        label={copy.channel}
        options={ERASURE_REQUEST_CHANNELS.map((value) => ({
          value,
          label: m.privacy.channelNames[value],
        }))}
        value={channel}
        onChange={setChannel}
      />
      <OwnerCodeField className={FIELD} label={m.privacy.code} value={code} onChange={setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/** A request put back within its 24 hours: as if it had never been recorded. */
function WithdrawDialog({ dialogRef, panel, onDone }: DialogProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.privacy.withdraw
  const { send, busy, problem, clear } = useSend()
  const { request } = panel

  async function confirm() {
    const next = await send(() =>
      call(
        api.POST('/v1/staff/customers/{customerId}/privacy/request/withdraw', {
          params: { path: { customerId: panel.customerId } },
        }),
      ),
    )
    if (!next) return
    dialogRef.current?.close()
    onDone(next, copy.done)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={copy.title}
      confirmLabel={copy.confirm}
      cancelLabel={copy.keep}
      busy={busy}
      onConfirm={() => void confirm()}
      onClose={clear}
    >
      {request ? (
        <p>
          {format(copy.body, {
            date: formatDate(locale, request.requestedOn, 'short'),
            until: formatDateTime(locale, request.graceEndsAt),
          })}
        </p>
      ) : null}
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/** A legal hold, with its reason from the closed list: nothing of the customer's goes while it stands. */
function HoldDialog({ dialogRef, panel, onDone }: DialogProps) {
  const { messages: m } = useLocale()
  const copy = m.privacy.legalHold
  const { send, busy, problem, clear } = useSend()
  const [reason, setReason] = useState<LegalHoldReason | null>(null)

  async function confirm() {
    if (!reason) return
    const next = await send(() =>
      call(
        api.POST('/v1/staff/customers/{customerId}/privacy/hold', {
          params: { path: { customerId: panel.customerId } },
          body: { reason },
        }),
      ),
    )
    if (!next) return
    dialogRef.current?.close()
    setReason(null)
    onDone(next, copy.done)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={copy.title}
      confirmLabel={copy.confirm}
      cancelLabel={m.privacy.cancel}
      busy={busy}
      ready={reason !== null}
      reasons={[copy.why]}
      onConfirm={() => void confirm()}
      onClose={clear}
    >
      <p>{copy.body}</p>
      <ChoiceChips
        name="legal-hold-reason"
        label={copy.reason}
        options={LEGAL_HOLD_REASONS.map((value) => ({
          value,
          label: m.privacy.reasonNames[value],
        }))}
        value={reason}
        onChange={setReason}
      />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/** The hold lifted, with the Owner's code: the data follows the retention rules again. */
function LiftDialog({ dialogRef, panel, onDone }: DialogProps) {
  const { locale, messages: m } = useLocale()
  const copy = m.privacy.lift
  const { send, busy, problem, clear } = useSend()
  const [code, setCode] = useState('')
  const { legalHold } = panel

  async function confirm() {
    if (!OWNER_CODE.test(code)) return
    const next = await send(() =>
      call(
        api.POST('/v1/staff/customers/{customerId}/privacy/hold/lift', {
          params: { path: { customerId: panel.customerId } },
          body: { code },
        }),
      ),
    )
    setCode('')
    if (!next) return
    dialogRef.current?.close()
    onDone(next, copy.done)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={copy.title}
      confirmLabel={copy.confirm}
      cancelLabel={m.privacy.cancel}
      busy={busy}
      ready={OWNER_CODE.test(code)}
      reasons={[copy.why]}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        setCode('')
      }}
    >
      {legalHold ? (
        <p>
          {format(copy.body, {
            date: romeDay(locale, legalHold.at),
            reason: m.privacy.reasons[legalHold.reason],
          })}
        </p>
      ) : null}
      <OwnerCodeField className={FIELD} label={m.privacy.code} value={code} onChange={setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}

/**
 * A copy of the customer's data (access, art. 15; portability, art. 20): what
 * they ask for, the day the request arrived, whether their ID photos go in,
 * and the Owner's code. The answer is a ticket for this session, good for a
 * minute: the browser saves the archive from its link at once, to disk, never
 * holding it in the page. Nothing is chosen for the Owner: what they ask for
 * and the photos are asked each time.
 */
function ExportDialog({
  dialogRef,
  customerId,
  onDone,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  customerId: string
  onDone: (text: string) => void
}) {
  const { messages: m } = useLocale()
  const copy = m.privacy.export
  const asked = m.privacy.request
  const today = useRomeToday()
  const { send, busy, problem, clear } = useSend()
  const [purpose, setPurpose] = useState<DataExportPurpose | null>(null)
  const [requestedOn, setRequestedOn] = useState(today)
  const [documents, setDocuments] = useState<'include' | 'leave' | null>(null)
  const [code, setCode] = useState('')
  const reasons = [
    ...(purpose ? [] : [copy.why.purpose]),
    ...(ISO_DATE.test(requestedOn) && requestedOn <= today ? [] : [asked.why.day]),
    ...(documents ? [] : [copy.why.documents]),
    ...(OWNER_CODE.test(code) ? [] : [asked.why.code]),
  ]

  async function confirm() {
    if (!purpose || !documents || reasons.length > 0) return
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/customers/{customerId}/export', {
          params: { path: { customerId } },
          body: { purpose, requestedOn, documents: documents === 'include', code },
        }),
      ),
    )
    // A code is spent whether or not the server took it: a new one is typed either way.
    setCode('')
    if (!answer) return
    saveFromLink(filePath({ kind: 'export', customerId, ticket: answer.ticket }))
    dialogRef.current?.close()
    setPurpose(null)
    setDocuments(null)
    setRequestedOn(today)
    onDone(copy.done)
  }

  return (
    <ConfirmDialog
      dialogRef={dialogRef}
      title={copy.title}
      confirmLabel={copy.confirm}
      cancelLabel={m.privacy.cancel}
      busy={busy}
      ready={reasons.length === 0}
      reasons={reasons}
      onConfirm={() => void confirm()}
      onClose={() => {
        clear()
        setCode('')
      }}
      className="max-w-lg"
    >
      <p>{copy.body}</p>
      <ChoiceChips
        name="export-purpose"
        label={copy.purpose}
        options={DATA_EXPORT_PURPOSES.map((value) => ({ value, label: copy.purposes[value] }))}
        value={purpose}
        onChange={setPurpose}
      />
      <Field
        className={FIELD}
        label={asked.requestedOn}
        hint={asked.requestedOnHint}
        required
        error={requestedOn !== '' && requestedOn > today ? asked.why.day : null}
      >
        {(control) => (
          <StaffInput
            {...control}
            type="date"
            max={today}
            value={requestedOn}
            onChange={(event) => setRequestedOn(event.target.value)}
          />
        )}
      </Field>
      <ChoiceChips
        name="export-documents"
        label={copy.documents}
        options={(['include', 'leave'] as const).map((value) => ({
          value,
          label: copy.documentsChoices[value],
        }))}
        value={documents}
        onChange={setDocuments}
      />
      <p className="text-sm text-text-muted">{copy.documentsHint}</p>
      <Notice tone="warning">
        <p>{copy.care}</p>
      </Notice>
      <OwnerCodeField className={FIELD} label={m.privacy.code} value={code} onChange={setCode} />
      <ProblemNotice problem={problem} />
    </ConfirmDialog>
  )
}
